import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function parseDate(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return new Date(0);
  // Suporta formato YYYY-MM-DD (Django) e DD/MM/YYYY
  if (dateStr.includes('-')) {
    return new Date(dateStr);
  }
  const [day, month, year] = dateStr.split('/').map(Number);
  return new Date(year, month - 1, day);
}

export const useBetsData = (selectedMonth = null, selectedYear = null) => {
  const [betsData, setBetsData] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAvailableMonths = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/bets/available_months/`);
      if (response.ok) {
        const data = await response.json();
        setAvailableMonths(data);
      }
    } catch (err) {
      console.error('Erro ao carregar meses disponíveis:', err);
    }
  }, []);

  const fetchBets = useCallback(async () => {
    try {
      let url = `${API_URL}/bets/?limit=1000`;
      
      // Adiciona filtro por mês se selecionado
      if (selectedMonth && selectedYear) {
        url += `&month=${selectedMonth}&year=${selectedYear}`;
      }
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      const data = await response.json();
      console.log('📊 Dados da API:', data);
      // API Django retorna paginado com results ou array direto
      const rawBets = data.results || data;
      // Mapear campos Django (snake_case) para frontend (camelCase)
      const bets = rawBets.map(bet => ({
        ...bet,
        totalOdd: parseFloat(bet.total_odd) || 0,
        amount: parseFloat(bet.amount) || 0,
        addedAmount: parseFloat(bet.added_amount) || 0,
        games: (bet.games || []).map(game => ({
          ...game,
          gameOdd: parseFloat(game.game_odd) || 0,
          combinedOdd: parseFloat(game.combined_odd) || parseFloat(game.game_odd) || 0,
          homeTeam: game.home_team || '',
          awayTeam: game.away_team || null,
          pickDescription: game.pick_description || '',
          gameResult: game.result || '',
          extraPicks: (game.extra_picks || []).map(extra => ({
            ...extra,
            odd: parseFloat(extra.odd) || 0,
            pickDescription: extra.pick_description || '',
          })),
        }))
      }));
      console.log('✅ Apostas:', bets);
      setBetsData(bets);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    fetchAvailableMonths();
  }, [fetchAvailableMonths]);

  useEffect(() => {
    const loadBets = async () => {
      setLoading(true);
      
      // Delay mínimo para mostrar loading spinner
      const minDelay = new Promise(resolve => setTimeout(resolve, 350));
      
      // Executa fetch e delay em paralelo
      await Promise.all([fetchBets(), minDelay]);
      
      setLoading(false);
    };
    
    loadBets();

    const interval = setInterval(fetchBets, 30000);

    return () => clearInterval(interval);
  }, [fetchBets]);

  const sortedBets = betsData.slice().sort((a, b) => {
    const dateDiff = parseDate(b.date) - parseDate(a.date);
    if (dateDiff !== 0) return dateDiff;
    return b.id - a.id;
  });

  const getSports = () => {
    const sportsSet = new Set();
    sortedBets.forEach(bet => {
      bet.games.forEach(game => {
        if (game.sport) sportsSet.add(game.sport);
      });
    });
    return Array.from(sportsSet);
  };

  const getResults = () => {
    const resultsSet = new Set();
    sortedBets.forEach(bet => {
      if (bet.result) resultsSet.add(bet.result);
    });
    return Array.from(resultsSet);
  };

  return { 
    betsData: sortedBets, 
    loading, 
    error,
    getSports,
    getResults,
    availableMonths,
    refetch: fetchBets
  };
};
